import { API_BASE } from '../config.js';
import type { Driver } from '@multirx/shared';
import type { DriverBackupFile } from '@multirx/shared/game/garageBackup.js';

export async function fetchDrivers(limit?: number): Promise<Driver[]> {
  const url = limit != null ? `${API_BASE}/api/drivers?limit=${limit}` : `${API_BASE}/api/drivers`;
  const res = await fetch(url);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors du chargement des pilotes');
  }
  return res.json() as Promise<Driver[]>;
}

export async function fetchDriverById(id: number): Promise<Driver> {
  const res = await fetch(`${API_BASE}/api/drivers/${id}`);
  if (!res.ok) {
    if (res.status === 404) throw new Error('Pilote non trouvé');
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors du chargement du pilote');
  }
  return res.json() as Promise<Driver>;
}

export async function createDriver(driver: Omit<Driver, 'id'>): Promise<Driver> {
  const res = await fetch(`${API_BASE}/api/drivers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(driver),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors de la création du pilote');
  }
  return res.json() as Promise<Driver>;
}

export async function updateDriver(id: number, driver: Omit<Driver, 'id'>): Promise<Driver> {
  const res = await fetch(`${API_BASE}/api/drivers/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(driver),
  });
  if (!res.ok) {
    if (res.status === 404) throw new Error('Pilote non trouvé');
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors de la mise à jour');
  }
  return res.json() as Promise<Driver>;
}

/** Admin : attribue une voiture au pilote. */
export async function assignDriverCar(driverId: number, carId: number): Promise<Driver> {
  const res = await fetch(`${API_BASE}/api/drivers/${driverId}/assigned-car`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ carId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors de l’attribution');
  }
  return res.json() as Promise<Driver>;
}

/** Supprime un pilote (admin). */
export async function deleteDriver(id: number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/drivers/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    if (res.status === 404) throw new Error('Pilote non trouvé');
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors de la suppression');
  }
}

export async function uploadDriverImage(id: number, file: File): Promise<Driver> {
  const formData = new FormData();
  formData.append('image', file);
  const res = await fetch(`${API_BASE}/api/drivers/${id}/image`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? "Erreur lors de l'upload de l'image");
  }
  return res.json() as Promise<Driver>;
}

export async function assignDriverImageFromLibrary(id: number, imagePath: string): Promise<Driver> {
  const res = await fetch(`${API_BASE}/api/drivers/${id}/image-path`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ imagePath }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? "Erreur lors de l'assignation de l'image");
  }
  return res.json() as Promise<Driver>;
}

export interface ImportDriversResult {
  imported: number;
  drivers: Driver[];
}

export async function importDriversBackup(payload: DriverBackupFile): Promise<ImportDriversResult> {
  const res = await fetch(`${API_BASE}/api/drivers/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors de l’import des pilotes');
  }
  return res.json() as Promise<ImportDriversResult>;
}
